/**
 * Particle Emitter Behavior
 * 
 * Creates a continuous particle emitter at the mesh location.
 * 
 * Blender Setup:
 *   hm_behaviors: 'emitter:particle'
 *   hm_behavior_rate: 100
 *   hm_behavior_lifetimeMin: 1.0
 *   hm_behavior_lifetimeMax: 3.0
 *   hm_behavior_sizeMin: 0.1
 *   hm_behavior_sizeMax: 0.5
 *   hm_behavior_texture: 'assets/particles/flare.png'
 */

import { Vector3, Color4, ParticleSystem, Texture, RawTexture, type AbstractMesh, type Scene } from '@babylonjs/core';
import { EntityType } from '../../types/EntityTypes';
import type { EntityBehavior } from '../../core/BehaviorRegistry';
import type { BehaviorParameterSchema } from '../../core/BehaviorSchema';
import { logger } from '../../core/Logger';
import { ParticleManager, type ParticleSystemConfig } from '../../systems/ParticleManager';

// Store particle system references for cleanup
const particleSystemsByEntity = new Map<string, ParticleSystem>();

// Cached default texture (shared across all particle systems)
let cachedDefaultTexture: Texture | null = null;

/**
 * Create a default particle texture (white circle with soft falloff)
 */
function createDefaultParticleTexture(scene: Scene): Texture {
  if (cachedDefaultTexture) {
    return cachedDefaultTexture;
  }
  
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  const center = size / 2;
  
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = x - center;
      const dy = y - center;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const normalized = dist / center;
      
      // Soft circle falloff
      const alpha = Math.max(0, 1 - normalized * normalized);
      
      const idx = (y * size + x) * 4;
      data[idx] = 255;     // R
      data[idx + 1] = 255; // G
      data[idx + 2] = 255; // B
      data[idx + 3] = Math.floor(alpha * 255); // A
    }
  }
  
  cachedDefaultTexture = RawTexture.CreateRGBATexture(
    data,
    size,
    size,
    scene,
    false, // generateMipMaps
    false, // invertY
    Texture.BILINEAR_SAMPLINGMODE
  );
  cachedDefaultTexture.name = 'default_particle_flare';
  cachedDefaultTexture.hasAlpha = true;
  
  return cachedDefaultTexture;
}

/**
 * Particle Emitter Parameters Schema
 * 
 * Maps to Blender's native ParticleSettings where possible:
 * - rate → ParticleSettings.count / (frame_end - frame_start) 
 * - lifetime → ParticleSettings.lifetime
 * - size → ParticleSettings.particle_size
 * - velocity → ParticleSettings.normal_factor
 * 
 * For Blender workflow: artists can either use native particle systems
 * (export hook extracts values) or set hm_behavior_* properties directly.
 */
const particleEmitterParameters: BehaviorParameterSchema = {
  // ===== Preset =====
  preset: {
    type: 'enum',
    default: 'custom',
    enumValues: ['custom', 'fairies', 'rising_dust', 'impact_wood', 'dust_puff', 'fire_burst'],
    description: 'Use a built-in preset (overrides manual settings)',
    group: 'Preset',
  },

  // ===== Emission =====
  rate: {
    type: 'number',
    default: 100,
    min: 1,
    max: 2000,
    description: 'Particles emitted per second',
    group: 'Emission',
  },
  capacity: {
    type: 'number',
    default: 1000,
    min: 10,
    max: 10000,
    description: 'Maximum particles alive at once',
    group: 'Emission',
  },

  // ===== Lifetime =====
  lifetimeMin: {
    type: 'number',
    default: 1.0,
    min: 0.1,
    max: 60,
    step: 0.1,
    description: 'Minimum particle lifetime in seconds',
    group: 'Lifetime',
  },
  lifetimeMax: {
    type: 'number',
    default: 3.0,
    min: 0.1,
    max: 60,
    step: 0.1,
    description: 'Maximum particle lifetime in seconds',
    group: 'Lifetime',
  },

  // ===== Size =====
  sizeMin: {
    type: 'number',
    default: 0.1,
    min: 0.01,
    max: 10,
    step: 0.01,
    description: 'Minimum particle size',
    group: 'Size',
  },
  sizeMax: {
    type: 'number',
    default: 0.5,
    min: 0.01,
    max: 10,
    step: 0.01,
    description: 'Maximum particle size',
    group: 'Size',
  },

  // ===== Velocity =====
  emitPowerMin: {
    type: 'number',
    default: 0.5,
    min: 0,
    max: 50,
    step: 0.1,
    description: 'Minimum emit velocity/power',
    group: 'Velocity',
  },
  emitPowerMax: {
    type: 'number',
    default: 2.0,
    min: 0,
    max: 50,
    step: 0.1,
    description: 'Maximum emit velocity/power',
    group: 'Velocity',
  },
  direction1X: {
    type: 'number',
    default: 0,
    min: -1,
    max: 1,
    step: 0.1,
    description: 'Direction 1 X component',
    group: 'Velocity',
  },
  direction1Y: {
    type: 'number',
    default: 1,
    min: -1,
    max: 1,
    step: 0.1,
    description: 'Direction 1 Y component (up)',
    group: 'Velocity',
  },
  direction1Z: {
    type: 'number',
    default: 0,
    min: -1,
    max: 1,
    step: 0.1,
    description: 'Direction 1 Z component',
    group: 'Velocity',
  },
  direction2X: {
    type: 'number',
    default: 0,
    min: -1,
    max: 1,
    step: 0.1,
    description: 'Direction 2 X component',
    group: 'Velocity',
  },
  direction2Y: {
    type: 'number',
    default: 1,
    min: -1,
    max: 1,
    step: 0.1,
    description: 'Direction 2 Y component (up)',
    group: 'Velocity',
  },
  direction2Z: {
    type: 'number',
    default: 0,
    min: -1,
    max: 1,
    step: 0.1,
    description: 'Direction 2 Z component',
    group: 'Velocity',
  },
  gravityX: {
    type: 'number',
    default: 0,
    min: -50,
    max: 50,
    step: 0.1,
    description: 'Gravity X component (world units/sec²)',
    group: 'Velocity',
  },
  gravityY: {
    type: 'number',
    default: 0,
    min: -50,
    max: 50,
    step: 0.1,
    description: 'Gravity Y component (negative = down, e.g. -9.81)',
    group: 'Velocity',
  },
  gravityZ: {
    type: 'number',
    default: 0,
    min: -50,
    max: 50,
    step: 0.1,
    description: 'Gravity Z component (world units/sec²)',
    group: 'Velocity',
  },

  // ===== Color =====
  color1R: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Start color Red', group: 'Color' },
  color1G: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Start color Green', group: 'Color' },
  color1B: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Start color Blue', group: 'Color' },
  color1A: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Start color Alpha', group: 'Color' },
  color2R: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'End color Red', group: 'Color' },
  color2G: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'End color Green', group: 'Color' },
  color2B: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'End color Blue', group: 'Color' },
  color2A: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'End color Alpha', group: 'Color' },
  colorDeadR: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Dead color Red', group: 'Color' },
  colorDeadG: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Dead color Green', group: 'Color' },
  colorDeadB: { type: 'number', default: 1, min: 0, max: 1, step: 0.01, description: 'Dead color Blue', group: 'Color' },
  colorDeadA: { type: 'number', default: 0, min: 0, max: 1, step: 0.01, description: 'Dead color Alpha (0=fade out)', group: 'Color' },

  // ===== Appearance =====
  texture: {
    type: 'string',
    default: '',
    description: 'Particle texture path (e.g., assets/particles/flare.png)',
    group: 'Appearance',
  },
  blendMode: {
    type: 'enum',
    default: 'add',
    enumValues: ['add', 'standard', 'multiply'],
    description: 'Particle blend mode',
    group: 'Appearance',
  },

  // ===== Emitter Shape =====
  emitterType: {
    type: 'enum',
    default: 'point',
    enumValues: ['point', 'box', 'sphere'],
    description: 'Shape of the emission area',
    group: 'Emitter Shape',
  },
  emitterSizeX: {
    type: 'number',
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.1,
    description: 'Box emitter size X',
    group: 'Emitter Shape',
  },
  emitterSizeY: {
    type: 'number',
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.1,
    description: 'Box emitter size Y',
    group: 'Emitter Shape',
  },
  emitterSizeZ: {
    type: 'number',
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.1,
    description: 'Box emitter size Z',
    group: 'Emitter Shape',
  },
  emitterRadius: {
    type: 'number',
    default: 1.0,
    min: 0.01,
    max: 100,
    step: 0.1,
    description: 'Sphere emitter radius',
    group: 'Emitter Shape',
  },

  // ===== State =====
  enabled: {
    type: 'boolean',
    default: true,
    description: 'Start emitting on spawn',
    group: 'State',
  },
  preWarm: {
    type: 'boolean',
    default: true,
    description: 'Fast-forward to equilibrium state on start (particles appear immediately)',
    group: 'State',
  },
  preWarmCycles: {
    type: 'number',
    default: 100,
    min: 1,
    max: 500,
    description: 'Number of simulation cycles to pre-warm (higher = fuller appearance)',
    group: 'State',
  },
};

/**
 * Extract particle emitter parameters from mesh extras.
 * Uses hm_behavior_* properties set on the object.
 */
function extractParticleParams(mesh: AbstractMesh): ParticleSystemConfig & { preset?: string } {
  const extras = mesh.metadata?.gltf?.extras || mesh.metadata || {};
  
  return {
    name: mesh.name,
    preset: extras.hm_behavior_preset ?? 'custom',
    
    // Core Properties
    emitRate: extras.hm_behavior_rate ?? 100,
    capacity: extras.hm_behavior_capacity ?? 1000,
    
    // Lifetime & Size
    lifetime: {
      min: extras.hm_behavior_lifetimeMin ?? 1.0,
      max: extras.hm_behavior_lifetimeMax ?? 3.0,
    },
    size: {
      min: extras.hm_behavior_sizeMin ?? 0.1,
      max: extras.hm_behavior_sizeMax ?? 0.5,
    },
    
    // Velocity & Direction
    velocity: {
      min: extras.hm_behavior_emitPowerMin ?? 0.5,
      max: extras.hm_behavior_emitPowerMax ?? 2.0,
    },
    direction1: new Vector3(
      extras.hm_behavior_direction1X ?? 0,
      extras.hm_behavior_direction1Y ?? 1,
      extras.hm_behavior_direction1Z ?? 0
    ),
    direction2: new Vector3(
      extras.hm_behavior_direction2X ?? 0,
      extras.hm_behavior_direction2Y ?? 1,
      extras.hm_behavior_direction2Z ?? 0
    ),
    gravity: new Vector3(
      extras.hm_behavior_gravityX ?? 0,
      extras.hm_behavior_gravityY ?? 0,
      extras.hm_behavior_gravityZ ?? 0
    ),
    
    // Appearance
    color1: new Color4(
      extras.hm_behavior_color1R ?? 1,
      extras.hm_behavior_color1G ?? 1,
      extras.hm_behavior_color1B ?? 1,
      extras.hm_behavior_color1A ?? 1
    ),
    color2: new Color4(
      extras.hm_behavior_color2R ?? 1,
      extras.hm_behavior_color2G ?? 1,
      extras.hm_behavior_color2B ?? 1,
      extras.hm_behavior_color2A ?? 1
    ),
    colorDead: new Color4(
      extras.hm_behavior_colorDeadR ?? 1,
      extras.hm_behavior_colorDeadG ?? 1,
      extras.hm_behavior_colorDeadB ?? 1,
      extras.hm_behavior_colorDeadA ?? 0
    ),
    texturePath: extras.hm_behavior_texture ?? '',
    blendMode: getBlendMode(extras.hm_behavior_blendMode ?? 'add'),
    
    // Emitter Shape
    emitterType: (extras.hm_behavior_emitterType ?? 'point') as 'point' | 'box' | 'sphere',
    emitterSize: new Vector3(
      extras.hm_behavior_emitterSizeX ?? 1,
      extras.hm_behavior_emitterSizeY ?? 1,
      extras.hm_behavior_emitterSizeZ ?? 1
    ),
    emitterRadius: extras.hm_behavior_emitterRadius ?? 1.0,
    
    // State
    enabled: extras.hm_behavior_enabled ?? true,
    preWarm: extras.hm_behavior_preWarm ?? true,
    preWarmCycles: extras.hm_behavior_preWarmCycles ?? 100,
  };
}

function getBlendMode(mode: string): number {
  switch (mode) {
    case 'standard':
      return ParticleSystem.BLENDMODE_STANDARD;
    case 'multiply':
      return ParticleSystem.BLENDMODE_MULTIPLY;
    case 'add':
    default:
      return ParticleSystem.BLENDMODE_ADD;
  }
}

export const particleEmitterBehavior: EntityBehavior = {
  id: 'emitter:particle',
  displayName: 'Particle Emitter',
  description: 'Creates a continuous particle emitter at the mesh location. Can use Blender native particle settings or manual configuration.',
  type: EntityType.PARTICLE_EMITTER,
  category: 'object',
  tags: ['particle', 'emitter', 'effect'],
  parameters: particleEmitterParameters,
  
  onSpawn: (api) => {
    const mesh = api.self.mesh;
    if (!mesh) {
      logger.level.warn(`particle_emitter behavior: No mesh for entity ${api.self.id}`);
      return;
    }
    
    const config = extractParticleParams(mesh);
    const scene = mesh.getScene();
    
    // Hide marker mesh first
    mesh.isVisible = false;
    mesh.checkCollisions = false;
    
    // Check if using a preset
    if (config.preset && config.preset !== 'custom') {
      logger.level.debug(`particle_emitter: Using preset '${config.preset}' for ${mesh.name}`);
      
      // Create continuous emitter from preset
      const particleManager = ParticleManager.getInstance();
      if (particleManager) {
        const ps = particleManager.createContinuousFromPreset(config.preset, mesh, scene);
        
        if (ps) {
          particleSystemsByEntity.set(api.self.id, ps);
          if (config.enabled) {
            ps.start();
            logger.level.debug(`particle_emitter: Started preset ${api.self.id}`);
          }
          return; // Successfully used preset
        } else {
          logger.level.warn(`particle_emitter: Unknown preset '${config.preset}', falling back to custom`);
        }
      } else {
        logger.level.warn(`particle_emitter: ParticleManager not initialized, falling back to custom`);
      }
    }
    
    logger.level.debug(`particle_emitter: Creating custom particle system for ${mesh.name}`, config);
    
    // Create Babylon.js ParticleSystem
    const ps = new ParticleSystem(config.name, config.capacity, scene);
    
    // Set emitter
    ps.emitter = mesh;
    
    // Configure emission
    ps.emitRate = config.emitRate;
    ps.minLifeTime = config.lifetime.min;
    ps.maxLifeTime = config.lifetime.max;
    ps.minSize = config.size.min;
    ps.maxSize = config.size.max;
    ps.minEmitPower = config.velocity.min;
    ps.maxEmitPower = config.velocity.max;
    
    // Direction
    ps.direction1 = config.direction1;
    ps.direction2 = config.direction2;
    
    // Gravity
    if (config.gravity) {
      ps.gravity = config.gravity;
    }
    
    // Colors
    ps.color1 = config.color1;
    ps.color2 = config.color2;
    ps.colorDead = config.colorDead;
    
    // Blend mode
    ps.blendMode = config.blendMode ?? ParticleSystem.BLENDMODE_ADD;
    
    // Emitter shape
    switch (config.emitterType) {
      case 'box':
        ps.minEmitBox = config.emitterSize!.scale(-0.5);
        ps.maxEmitBox = config.emitterSize!.scale(0.5);
        break;
      case 'sphere':
        ps.createSphereEmitter(config.emitterRadius ?? 1.0);
        break;
      case 'point':
      default:
        ps.minEmitBox = Vector3.Zero();
        ps.maxEmitBox = Vector3.Zero();
        break;
    }
    
    // Texture - use provided or create default flare
    if (config.texturePath) {
      ps.particleTexture = new Texture(config.texturePath, scene);
    } else {
      // Create a default white circle/flare texture procedurally
      ps.particleTexture = createDefaultParticleTexture(scene);
    }
    
    // Pre-warm to equilibrium state (particles appear immediately filled)
    if (config.preWarm) {
      ps.preWarmCycles = config.preWarmCycles ?? 100;
      ps.preWarmStepOffset = 10; // ms per cycle
    }
    
    // Store reference
    particleSystemsByEntity.set(api.self.id, ps);
    
    // Start if enabled
    if (config.enabled) {
      ps.start();
      logger.level.debug(`particle_emitter: Started ${api.self.id} (preWarm=${config.preWarm})`);
    }
  },
  
  onDestroy: (api) => {
    const ps = particleSystemsByEntity.get(api.self.id);
    if (ps) {
      ps.stop();
      ps.dispose();
      particleSystemsByEntity.delete(api.self.id);
      logger.level.debug(`particle_emitter: Disposed ${api.self.id}`);
    }
  },
};
